import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import { RequirementStatus } from "@wingspanhq/payments/dist/interfaces/requirement";
import {
  ISignedDocument,
  ISignedDocumentResponse,
  SignerRole
} from "@wingspanhq/signed-documents/dist/lib/interfaces";
import { useMutationDownloadClientDocument } from "../../../../../../query/files/mutations";
import { useMutationSignSignedDocument } from "../../../../../../query/signedDocuments/mutations/useMutationSignSignedDocument";
import { getRequirementDefinitionTableCellProps } from "../../../../../RequirementDefinitions/utils";
import { selectorIsRequirementReadyToSignAsPayee } from "../../../../selectors/selectorIsRequirementReadyToSignAsPayee";
import { selectorIsRequirementReadyToSignAsPayer } from "../../../../selectors/selectorIsRequirementReadyToSignAsPayer";
import {
  CellsConfig,
  ContextItem,
  RequirementAction,
  RequirementTableCallback
} from "../../../../types";
import { SignaturesFieldsWidget } from "../../components/SignaturesFieldsWidget";
import { getSignaturePayerRequirementStatus } from "../helpers/getSignaturePayerRequirementStatus";
import { getSignatureRequirementName } from "../helpers/getSignatureRequirementName";
import { getSignedDocumentProgressSteps } from "../helpers/getSignedDocumentProgressSteps";
import { SIGNATURE_REQUIREMENT_EVENT } from "../utils";
import { payeeSignatureRenderCardDescription } from "./payeeSignatureRenderCardDescription";
import { getRequirementEngagementsTableCellProps } from "../../../../utils";

export const usePayeeSignatureRequirementConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  IRequirementResponse,
  ContextItem,
  ISignedDocument | undefined
> => {
  const [signDocument] = useMutationSignSignedDocument();
  const [downloadPDFAsPayer, downloadPDFAsPayerMeta] =
    useMutationDownloadClientDocument();

  const handleRequirementDownload = async (
    requirement: IRequirementResponse
  ) => {
    await downloadPDFAsPayer(
      {
        documentId: requirement.dataSourceId!
      },
      {
        onSuccess() {
          callback(SIGNATURE_REQUIREMENT_EVENT.DownloadAsPayer, requirement);
        }
      }
    );
  };

  const handleRequirementCountersign = async (
    requirement: IRequirementResponse,
    document: ISignedDocumentResponse
  ) => {
    await signDocument(document, {
      onSuccess() {
        callback(SIGNATURE_REQUIREMENT_EVENT.SignAsPayer, requirement);
      }
    });
  };

  const getDownloadAction = (
    requirement: IRequirementResponse,
    context: ContextItem,
    signedDocument: ISignedDocumentResponse | undefined
  ) => {
    const isDocumentReadyToDownload =
      requirement.dataSourceId &&
      requirement.dataSourceStatus === RequirementStatus.Complete;

    if (isDocumentReadyToDownload) {
      return async () => {
        await handleRequirementDownload(requirement);
      };
    }

    return undefined;
  };

  const getCounterSignAction = (
    requirement: IRequirementResponse,
    context: ContextItem,
    signedDocument: ISignedDocumentResponse | undefined
  ) => {
    if (selectorIsRequirementReadyToSignAsPayer(requirement, signedDocument)) {
      return async () => {
        await handleRequirementCountersign(requirement, signedDocument!);
      };
    }

    return undefined;
  };

  return {
    isActionRequired: (requirement, context, signedDocument) => {
      const counterSignAction = getCounterSignAction(
        requirement,
        context,
        signedDocument
      );

      return !!counterSignAction;
    },
    requirementStatusAlert: (requirement, context, signedDocument) => {
      if (
        selectorIsRequirementReadyToSignAsPayer(requirement, signedDocument)
      ) {
        return {
          theme: "warning",
          title: "Your signature required",
          children: "Your signature is required to complete the requirement.",
          icon: "time"
        };
      } else if (
        selectorIsRequirementReadyToSignAsPayee(requirement, signedDocument)
      ) {
        return {
          theme: "info",
          title: "Awaiting contractor",
          children: "Awaiting contractor to complete the requirement.",
          icon: "time"
        };
      }
    },
    requirementDetailsProgressWidget: (
      requirement,
      context,
      signedDocument
    ) => {
      return signedDocument
        ? getSignedDocumentProgressSteps(
            signedDocument.status,
            signedDocument.signatures.some(
              ({ signerRole }) => signerRole === SignerRole.Payer
            )
          )
        : undefined;
    },
    requirementDetailsWidgets: (requirement, context, signedDocument) => {
      if (signedDocument?.responseItems?.length) {
        return <SignaturesFieldsWidget signedDocument={signedDocument} />;
      }

      return null;
    },
    requirementName: getSignatureRequirementName,
    requirementDetails: (requirement, context, signedDocument) => {
      const downloadAction = getDownloadAction(
        requirement,
        context,
        signedDocument
      );

      const signAction = getCounterSignAction(
        requirement,
        context,
        signedDocument
      );

      return {
        header: {
          label: {
            text: getSignatureRequirementName(
              requirement,
              context,
              signedDocument
            )
          }
        },
        children: payeeSignatureRenderCardDescription(
          requirement,
          signedDocument
        ),
        data: [],
        actions:
          downloadAction || signAction
            ? {
                alignment: "fill",
                buttons: [
                  {
                    label: "Download file",
                    onAsyncClick: downloadAction,
                    kind: "Secondary",
                    visible: !!downloadAction
                  },
                  {
                    label: "Start",
                    onAsyncClick: signAction,
                    kind: "Primary",
                    visible: !!signAction
                  }
                ]
              }
            : undefined
      };
    },
    toolbarActions: (requirement, context, signedDocument) => {
      const actions: RequirementAction[] = [];

      const downloadAction = getDownloadAction(
        requirement,
        context,
        signedDocument
      );

      const counterSignAction = getCounterSignAction(
        requirement,
        context,
        signedDocument
      );

      if (counterSignAction) {
        actions.push({
          label: "Sign document",
          onAsyncClick: counterSignAction,
          buttonKind: "Primary",
          icon: "edit-line",
          buttonIcon: null,
          mainAction: true
        });
      }

      if (downloadAction) {
        actions.push({
          onAsyncClick: downloadAction,
          label: "Download file",
          buttonKind: counterSignAction ? undefined : "Secondary",
          icon: "download",
          buttonIcon: null
        });
      }

      return actions;
    },
    nameCell: (requirement, context, signedDocument) => {
      return {
        text: getSignatureRequirementName(requirement, context, signedDocument)
      };
    },
    typeCell: requirement =>
      getRequirementDefinitionTableCellProps({
        type: requirement.requirementType
      }),
    statusCell: (requirement, context, signedDocument) => {
      return getSignaturePayerRequirementStatus(
        requirement,
        context,
        signedDocument
      );
    },
    engagementsCell: getRequirementEngagementsTableCellProps
  };
};
